import type { OrderLineAddResultDto, ProductItem, PriceItem, AOrderLine } from "@edco/apollo";

// Base properties that are optional
export type GA4ItemBase = {
  affiliation?: string;
  coupon?: string;
  discount?: number;
  index?: number;
  item_brand?: string;
  item_category?: string;
  item_category2?: string;
  item_category3?: string;
  item_category4?: string;
  item_category5?: string;
  item_list_id?: string;
  item_list_name?: string;
  item_variant?: string;
  location_id?: string;
  price?: number;
  quantity?: number;
};

// Require at least one of item_id or item_name.
// This union says: either item_id is required (with an optional item_name)
// or item_name is required (with an optional item_id).
export type GA4Item =
  | ( { item_id: string; item_name?: string } & GA4ItemBase )
  | ( { item_name: string; item_id?: string } & GA4ItemBase );

export function useCommerceAnalytics () {
  const { proxy } = useScriptGoogleTagManager();
  const basketStore = useBasketStore();

  /**
   * Triggers a search event with the given query and updates the data layer for analytics tracking.
   *
   * @param  query - The search term entered by the user.
   */
  function onSearch ( query: string ) {
    proxy.dataLayer.push( { ecommerce: null } );
    proxy.dataLayer.push( {
      event: "search",
      ecommerce: {
        search_term: query,
      },
    } );
  }

  /**
   * Handles the action of selecting a product and sends the corresponding data to the data layer.
   *
   * @param product - The selected product, containing its details such as ID and name.
   * @param [listId] - (Optional) The ID of the list the product belongs to.
   * @param [listName] - (Optional) The name of the list the product belongs to.
   * @param [price] - (Optional) The price of the product to log.
   */
  function onSelectProduct ( product: ProductItem, listId?: string, listName?: string, price?: PriceItem ) {
    let priceValue = 0;
    if ( price && price.value ) {
      if ( price.valueType === "simple" ) {
        const simple = price.value;
        priceValue = simple.priceExcVat ?? 0;
      } else {
        // NFER don't use tiered or volume pricing
        console.warn( `Pricing type not handled: ${price.valueType}` );
      }
    }

    proxy.dataLayer.push( { ecommerce: null } );
    proxy.dataLayer.push( {
      event: "select_item",
      ecommerce: {
        item_list_id: listId,
        item_list_name: listName,
        items: <GA4Item[]>[
          {
            item_id: product.id.toString(),
            item_name: product.name,
            quantity: 1,
            price: priceValue,
          },
        ],
      },
    } );
  }

  /**
   * Tracks the view of a product and sends data to the data layer for analytics purposes.
   *
   * @param product - The product information to be viewed, including its ID and name.
   * @param [price] - Optional price information associated with the product.
   * @return This function does not return a value.
   */
  function onViewProduct ( product: ProductItem, price?: PriceItem ) {
    let priceValue = 0;
    if ( price && price.value ) {
      if ( price.valueType === "simple" ) {
        const simple = price.value;
        priceValue = simple.priceExcVat ?? 0;
      } else {
        // NFER don't use tiered or volume pricing
        console.warn( `Pricing type not handled: ${price.valueType}` );
      }
    }

    proxy.dataLayer.push( { ecommerce: null } );
    proxy.dataLayer.push( {
      event: "view_item",
      ecommerce: {
        currency: "GBP", // todo: support currencies
        value: priceValue, // sum of each item's unit price * quantity
        items: <GA4Item[]>[
          {
            item_id: product.id.toString(),
            item_name: product.name,
            quantity: 1,
            price: priceValue, // price is the unit price
          },
        ],
      },
    } );
  }

  /**
   * Handles the process of adding items to the basket, prepares the required data for analytics,
   * and pushes the event to the data layer.
   *
   * @param results - Array of results containing the details of order lines to be added to the basket.
   */
  function onAddToBasket ( results: OrderLineAddResultDto[] ) {
    const items: GA4Item[] = [];
    for ( const result of results ) {
      const line = basketStore.basketLines?.find( x => x.id === result.orderLineId );

      if ( !line ) {
        throw createError( { statusCode: 500, message: "Line not found" } );
      }

      const item = mapLineToItem( line );
      items.push( item );
    }

    proxy.dataLayer.push( { ecommerce: null } );
    proxy.dataLayer.push( {
      event: "add_to_cart",
      ecommerce: {
        currency: "GBP", // todo: support currencies
        value: getItemsValue( items ),
        items,
      },
    } );
  }

  /**
   * Handles the removal of items from the basket and pushes the corresponding
   * analytics event to the data layer.
   */
  function onRemoveFromBasket ( items: AOrderLine[] ) {
    const mapped = items.map( i => mapLineToItem( i ) );

    proxy.dataLayer.push( { ecommerce: null } );
    proxy.dataLayer.push( {
      event: "remove_from_cart",
      ecommerce: {
        currency: "GBP", // todo: support currencies
        value: getItemsValue( mapped ),
        items: mapped,
      },
    } );
  }

  /**
   * Triggers the 'begin_checkout' event for the ecommerce tracking system and updates
   * the dataLayer with the relevant information.
   *
   * The function processes the current items in the basket, maps them to a standardized
   * format, and sends the event along with necessary details such as the total value
   * and currency.
   */
  function onBeginCheckout () {
    const items = mapLinesToItems( basketStore.basketLines );

    proxy.dataLayer.push( { ecommerce: null } );
    proxy.dataLayer.push( {
      event: "begin_checkout",
      ecommerce: {
        currency: "GBP", // todo: support currencies
        value: getItemsValue( items ),
        items,
      },
    } );
  }

  const orderResource = $orderResource();

  /**
   * Handles the purchase event by pushing transaction and purchase details to the data layer.
   *
   * @param transactionId - The unique identifier for the transaction to be processed.
   */
  async function onPurchase ( transactionId: string ) {
    const basketId = useCookie( "apollo-payment-id" );
    if ( !basketId.value ) {
      throw createError( "Unable to log purchase event, missing basket id" );
    }
    const order = await orderResource.getOrder( basketId.value );
    const items = mapLinesToItems( order.orderLines );

    proxy.dataLayer.push( { ecommerce: null } );
    proxy.dataLayer.push( {
      event: "purchase",
      ecommerce: {
        currency: "GBP", // todo: support currencies
        value: getItemsValue( items ),
        transaction_id: transactionId,
        items,
      },
    } );
  }

  /**
   * Maps an array of order lines to an array of items using a mapping function.
   *
   * @param [lines] - An optional array of order line objects to be mapped.
   * @return An array of items derived from the given order lines, or undefined if no input is provided.
   */
  function mapLinesToItems ( lines?: AOrderLine[] ): GA4Item[] {
    return lines?.map( mapLineToItem ) ?? [];
  }

  /**
   * Maps an AOrderLine object to a simplified item representation.
   *
   * @param line - The order line object containing details about the product and its quantity.
   * @return An object representing the item with its name, ID, quantity, and price.
   */
  function mapLineToItem ( line: AOrderLine ): GA4Item {
    return {
      item_name: line.product!.name ?? "",
      item_id: line.product!.id!.toString(),
      quantity: line.quantity ?? 0,
      price: line.itemPrice ?? 0,
    };
  }

  /**
   * Calculates the total value of the provided items by multiplying their price and quantity,
   * and then summing up all these values.
   *
   * @param items - An array of GA4Item objects, where each object contains a price and quantity.
   * @return The total computed value of all items.
   */
  function getItemsValue ( items: GA4Item[] ) {
    return items
      .map( x => ( x.price ?? 0 ) * ( x.quantity ?? 0 ) )
      .reduce( ( a, b ) => a + b, 0 );
  }

  return {
    onSearch,
    onSelectProduct,
    onViewProduct,
    onAddToBasket,
    onRemoveFromBasket,
    onBeginCheckout,
    onPurchase,
  };
}
